import React from 'react';
import PropTypes from 'prop-types';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import classNames from 'classnames';

import DataTableCell from './DataTableCell';
import utils from './utils';
import DragHandle from './DragHandle/DragHandle';
import DataTableRow from './DataTableRow';

class DataTableBody extends React.Component {
    constructor(props) {
        super(props);

        this.onRowClick = this.onRowClick.bind(this);
        this.isDragDisabled = this.isDragDisabled.bind(this);
    }

    onRowClick(row, event) {
        const { target } = event;
        const { onRowClick } = this.props;
        const selection = window.getSelection();

        if (selection.toString() && target.contains(selection.anchorNode)) {
            return;
        }

        onRowClick(row, event);
    }

    isDragDisabled(index) {
        const { disabledDragRows } = this.props;

        return disabledDragRows.indexOf(index) > -1;
    }

    render() {
        const {
            columns,
            data,
            isDraggable,
            theme,
            activeId,
            stickyHeader,
            rowClickable,
        } = this.props;
        const rowClassName = classNames('data-table__row', {
            'data-table__row--active': theme === 'ACTIVE',
        });

        const style = stickyHeader ? {
            position: 'relative',
            top: '-53px',
        } : {};

        return (
            isDraggable ?
                <Droppable droppableId="table">
                    {(dropProvided, dropSnapshot) => {
                        const bodyClassName = classNames('data-table__body', {
                            'data-table__body--draggable': dropSnapshot.isDraggingOver,
                        });

                        return (
                            <tbody
                                className={bodyClassName}
                                ref={dropProvided.innerRef}
                            >
                                {
                                    data.map((row, idx) => {
                                        const rowKey = idx;
                                        const isDragDisabled = this.isDragDisabled(idx);

                                        return (
                                            <Draggable
                                                key={rowKey}
                                                draggableId={rowKey}
                                                index={idx}
                                                isDragDisabled={isDragDisabled}
                                            >
                                                {(dragProvided, snapshot) => (
                                                    <tr
                                                        ref={dragProvided.innerRef}
                                                        className={`${rowClassName} ${snapshot.isDragging ? 'data-table__row--draggable' : ''}`}
                                                        onClick={event =>
                                                            this.onRowClick(row, event)}
                                                        {...dragProvided.draggableProps}
                                                    >
                                                        <td className="data-table__cell data-table__cell--dnd">
                                                            <DragHandle
                                                                {...dragProvided.dragHandleProps}
                                                                isVisible={!isDragDisabled}
                                                            />
                                                        </td>
                                                        {columns.map((column, index) => {
                                                            const { accessor } = column;
                                                            const colKey =
                                                                utils.getColumnKey(accessor, index);
                                                            return (
                                                                <DataTableCell
                                                                    {...column}
                                                                    data={row}
                                                                    index={idx}
                                                                    key={colKey}
                                                                />
                                                            );
                                                        })}
                                                    </tr>
                                                )}
                                            </Draggable>
                                        );
                                    })
                                }
                            </tbody>
                        );
                    }}
                </Droppable>
                :
                <tbody className="data-table__body" style={style}>
                    {
                        data.map((row, idx) => {
                            const rowKey = idx;
                            const questionId = Object.prototype.hasOwnProperty.call(row, 'question_id') ?
                                row.question_id : null;
                            const isActive = activeId === questionId;
                            return (
                                <DataTableRow
                                    key={rowKey}
                                    theme={theme}
                                    row={row}
                                    columns={columns}
                                    isActive={isActive}
                                    idx={rowKey}
                                    onRowClick={this.onRowClick}
                                    rowClickable={rowClickable}
                                />
                            );
                        })
                    }
                </tbody>
        );
    }
}

DataTableBody.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        accessor: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.string,
        ]).isRequired,
        align: PropTypes.oneOf(['center', 'left', 'right']),
        className: PropTypes.string,
        Cell: PropTypes.oneOfType([
            PropTypes.element,
            PropTypes.func,
            PropTypes.string,
            PropTypes.bool,
        ]),
        title: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.bool,
        ]),
    })).isRequired,
    data: PropTypes.arrayOf(PropTypes.object),
    onRowClick: PropTypes.func.isRequired,
    isDraggable: PropTypes.bool.isRequired,
    disabledDragRows: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ])).isRequired,
    theme: PropTypes.oneOf(['ACTIVE', 'PASSIVE']),
    activeId: PropTypes.number,
    stickyHeader: PropTypes.bool,
    rowClickable: PropTypes.bool,
};

DataTableBody.defaultProps = {
    theme: 'ACTIVE',
    data: [],
    activeId: 0,
    stickyHeader: false,
    rowClickable: true,
};

export default DataTableBody;
